import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { partner } from '../../api/partner';
import { toast } from '../../app/toast';
import { PageHeader } from '../../components/PageHeader';
import { useT } from '../../i18n';
import { describeError } from '../../lib/errors';
import { COMMON_COUNTRIES, localName, useFormat } from '../../lib/format';
import { useAsync } from '../../lib/useAsync';

/** A partner's own case: few fields, sensible defaults (repatriation, Moldova as destination). */
export function CaseNewPage() {
  const t = useT();
  const f = useFormat();
  const nav = useNavigate();
  const meta = useAsync(() => partner.meta(), []);
  const services = meta.data?.service_types ?? [];
  const [service, setService] = useState('');
  const [deceased, setDeceased] = useState('');
  const [origin, setOrigin] = useState('IT');
  const [originPlace, setOriginPlace] = useState('');
  const [dest, setDest] = useState('MD');
  const [destPlace, setDestPlace] = useState('');
  const [custName, setCustName] = useState('');
  const [custPhone, setCustPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const countries = useMemo(
    () => COMMON_COUNTRIES.map((c) => ({ code: c, name: f.country(c) })).sort((a, b) => a.name.localeCompare(b.name, f.locale)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [f.locale],
  );
  const svc = service || services.find((s) => s.key === 'repatriation')?.key || services[0]?.key || 'repatriation';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!deceased.trim()) {
      setError(t('caseNew.required'));
      return;
    }
    setBusy(true);
    setError(null);
    const places = [originPlace.trim(), destPlace.trim()];
    try {
      const c = await partner.createCase({
        service_type_key: svc,
        title: places[0] || places[1] ? `${places[0] || f.country(origin)} → ${places[1] || f.country(dest)}` : undefined,
        deceased: { full_name: deceased.trim(), ...(places[0] ? { death_place: places[0] } : {}), death_country: origin },
        origin_country: origin,
        dest_country: dest,
        ...(custName.trim() || custPhone.trim()
          ? { customer: { ...(custName.trim() ? { name: custName.trim() } : {}), ...(custPhone.trim() ? { phone: custPhone.trim() } : {}) } }
          : {}),
      });
      toast(t('caseNew.created'));
      nav(`/cases/${c.id}`, { replace: true });
    } catch (err) {
      setError(t(describeError(err)));
    } finally {
      setBusy(false);
    }
  }

  const countrySelect = (id: string, value: string, set: (v: string) => void) => (
    <select id={id} className="input" value={value} onChange={(e) => set(e.target.value)}>
      {countries.map((c) => (
        <option key={c.code} value={c.code}>
          {c.name}
        </option>
      ))}
    </select>
  );

  return (
    <>
      <PageHeader title={t('caseNew.title')} back="/cases" />
      <form className="space-y-4" onSubmit={submit} noValidate>
        {services.length > 1 ? (
          <div>
            <label htmlFor="cn-svc" className="label">
              {t('caseNew.service')}
            </label>
            <select id="cn-svc" className="input" value={svc} onChange={(e) => setService(e.target.value)}>
              {services.map((s) => (
                <option key={s.key} value={s.key}>
                  {localName(s.name, f.locale) || s.key}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div>
          <label htmlFor="cn-dec" className="label">
            {t('caseNew.deceased')}
          </label>
          <input id="cn-dec" className="input" value={deceased} maxLength={300} onChange={(e) => setDeceased(e.target.value)} aria-invalid={!!error && !deceased.trim()} />
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label htmlFor="cn-o" className="label">
              {t('caseNew.origin')}
            </label>
            {countrySelect('cn-o', origin, setOrigin)}
          </div>
          <div>
            <label htmlFor="cn-op" className="label">
              {t('caseNew.originPlace')}
            </label>
            <input id="cn-op" className="input" value={originPlace} maxLength={200} onChange={(e) => setOriginPlace(e.target.value)} />
          </div>
          <div>
            <label htmlFor="cn-d" className="label">
              {t('caseNew.dest')}
            </label>
            {countrySelect('cn-d', dest, setDest)}
          </div>
          <div>
            <label htmlFor="cn-dp" className="label">
              {t('caseNew.destPlace')}
            </label>
            <input id="cn-dp" className="input" value={destPlace} maxLength={200} onChange={(e) => setDestPlace(e.target.value)} />
          </div>
        </div>
        <fieldset className="space-y-3">
          <legend className="sec mt-2">{t('caseNew.customer')}</legend>
          <div>
            <label htmlFor="cn-cn" className="label">
              {t('caseNew.customerName')}
            </label>
            <input id="cn-cn" className="input" value={custName} autoComplete="off" onChange={(e) => setCustName(e.target.value)} />
          </div>
          <div>
            <label htmlFor="cn-cp" className="label">
              {t('caseNew.customerPhone')}
            </label>
            <input id="cn-cp" className="input" inputMode="tel" autoComplete="off" value={custPhone} onChange={(e) => setCustPhone(e.target.value)} />
          </div>
        </fieldset>
        {error ? (
          <p role="alert" className="text-bad">
            {error}
          </p>
        ) : null}
        <button type="submit" className="btn-primary min-h-big w-full text-[18px]" disabled={busy}>
          {t('caseNew.submit')}
        </button>
      </form>
    </>
  );
}
